import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface HojoKamoku {
    id: number;
    kamoku_id: number;
    hojo_code: string;
    hojo_name: string;
}

interface HojoSelectProps {
    kamokuId: number | null;
    value: number | null;
    onChange: (hojoId: string) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    style?: React.CSSProperties;
    apiFetch?: (url: string, options?: any) => Promise<any>;
    currentTenantId?: number;
    disabled?: boolean;
    hasHojo?: boolean; // 親科目が補助科目ありかどうか
}

const HojoSelect: React.FC<HojoSelectProps> = ({
    kamokuId,
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    style,
    apiFetch,
    currentTenantId,
    disabled = false,
    hasHojo = false,
}) => {
    const [hojoList, setHojoList] = useState<HojoKamoku[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // 補助科目リストを取得
    useEffect(() => {
        if (!kamokuId || !apiFetch || !hasHojo) {
            setHojoList([]);
            setSearchTerm('');
            return;
        }

        let cancelled = false;
        const fetchHojo = async () => {
            setLoading(true);
            try {
                const tenantParam = currentTenantId ? `?tenant_id=${currentTenantId}` : '';
                const response = await apiFetch(`/api/hojo-kamoku/by-kamoku/${kamokuId}${tenantParam}`);
                if (!cancelled) {
                    if (response && typeof response.json === 'function') {
                        // apiFetch returns a Response object
                        const data = await response.json();
                        setHojoList(Array.isArray(data) ? data : []);
                    } else if (Array.isArray(response)) {
                        // Direct array (e.g. in tests)
                        setHojoList(response);
                    } else {
                        setHojoList([]);
                    }
                }
            } catch (error) {
                console.error('補助科目取得エラー:', error);
                if (!cancelled) setHojoList([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchHojo();
        return () => { cancelled = true; };
    }, [kamokuId, apiFetch, currentTenantId, hasHojo]);

    // ドロップダウン開閉時および値変更時に表示テキストを同期
    useEffect(() => {
        if (!isOpen) {
            // ドロップダウンが閉じている時は、実際のvalueに合致するテキストに強制補正する
            if (value && hojoList.length > 0) {
                const selected = hojoList.find(h => h.id === value);
                if (selected) {
                    setSearchTerm(`${selected.hojo_code} ${selected.hojo_name}`);
                } else {
                    setSearchTerm('');
                }
            } else {
                setSearchTerm('');
            }
        }
    }, [isOpen, value, hojoList]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return hojoList;

        // すでに選択済みの名称（文字列全体）と一致する場合、フィルターせず全件表示
        const selectedItem = hojoList.find(k => k.id != null && value != null && String(k.id) === String(value));
        if (selectedItem) {
            const selectedLabel = `${selectedItem.hojo_code} ${selectedItem.hojo_name}`;
            if (searchTerm === selectedLabel) {
                return hojoList;
            }
        }

        const lower = searchTerm.toLowerCase();
        return hojoList.filter(h => {
            const fullText = `${h.hojo_code} ${h.hojo_name}`.toLowerCase();
            return String(h.hojo_code || '').toLowerCase().includes(lower) ||
                   String(h.hojo_name || '').toLowerCase().includes(lower) ||
                   fullText.includes(lower);
        });
    }, [hojoList, searchTerm, value]);

    // ドロップダウンを開いたときに、既に選択済みの場合はその行をハイライトする（Enterで「なし」に置き換わらないようにする）
    useEffect(() => {
        if (isOpen && hojoList.length > 0) {
            if (value) {
                const idx = filteredOptions.findIndex((h) => h.id === value);
                setHighlightedIndex(idx >= 0 ? idx + 1 : 0);
            } else {
                setHighlightedIndex(0);
            }
        }
    }, [isOpen, value, filteredOptions, hojoList.length]);

    const isDisabled = disabled || !hasHojo || hojoList.length === 0;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (e.target.value === '') {
            onChange('');
        }
    };

    const handleSelect = (hojo: HojoKamoku, e?: React.KeyboardEvent) => {
        onChange(hojo.id.toString());
        setSearchTerm(`${hojo.hojo_code} ${hojo.hojo_name}`);
        setIsOpen(false);
        if (e && onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleClear = (e?: React.KeyboardEvent) => {
        onChange('');
        setSearchTerm('');
        setIsOpen(false);
        if (e && onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === ' ') {
                if (!isDisabled) {
                    setIsOpen(true);
                    e.preventDefault();
                }
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                if (value) {
                    handleClear();
                    e.preventDefault();
                    return;
                }
                if (onKeyDown) onKeyDown(e);
            } else if (onKeyDown) {
                onKeyDown(e);
            }
            return;
        }

        const allOptions = [{ id: -1, hojo_code: '', hojo_name: '（なし）', kamoku_id: 0 }, ...filteredOptions];

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => prev < allOptions.length - 1 ? prev + 1 : prev);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                
                // 1. まず、入力文字列がコードと完全一致するかチェック（最優先）
                if (searchTerm) {
                    const exactMatch = hojoList.find(k => k.hojo_code === searchTerm);
                    if (exactMatch) {
                        handleSelect(exactMatch, e);
                        return;
                    }
                }

                // 2. 「なし」が手動選択（矢印キーでハイライト）されている場合、または検索文字が空の場合
                if (highlightedIndex === 0) {
                    // 検索文字が入力されており、かつ一致する候補がある場合は、一番上の候補を自動選択
                    if (searchTerm && filteredOptions.length > 0) {
                        handleSelect(filteredOptions[0], e);
                    } else {
                        handleClear(e);
                    }
                    return;
                }

                // 3. 選択肢がある場合はハイライト中のものを選択
                if (filteredOptions.length > 0 && highlightedIndex > 0) {
                    handleSelect(filteredOptions[highlightedIndex - 1], e);
                    return; // 確定後はここで終了
                }

                // 上記に該当しなければ、ドロップダウンを閉じて親コンポーネントへEnterを伝播
                setIsOpen(false);
                if (onKeyDown) {
                    onKeyDown(e);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                if (onKeyDown) onKeyDown(e);
                break;
            default:
                break;
        }
    };

    // ドロップダウン位置計算
    useEffect(() => {
        const updatePosition = () => {
            if (isOpen && inputRef.current) {
                const rect = inputRef.current.getBoundingClientRect();
                setDropdownPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: Math.max(250, rect.width),
                });
            }
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen]);

    // 外側クリックで閉じる
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                if (listRef.current && listRef.current.contains(event.target as Node)) {
                    return;
                }
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // スクロール追従
    useEffect(() => {
        if (isOpen && listRef.current) {
            const activeItem = listRef.current.children[highlightedIndex] as HTMLElement;
            if (activeItem) {
                const listTop = listRef.current.scrollTop;
                const listHeight = listRef.current.clientHeight;
                const itemTop = activeItem.offsetTop;
                const itemHeight = activeItem.clientHeight;
                if (itemTop < listTop) {
                    listRef.current.scrollTop = itemTop;
                } else if (itemTop + itemHeight > listTop + listHeight) {
                    listRef.current.scrollTop = itemTop + itemHeight - listHeight;
                }
            }
        }
    }, [highlightedIndex, isOpen]);

    return (
        <div
            ref={containerRef}
            style={{ ...style, position: 'relative', display: 'inline-block' }}
        >
            <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onClick={() => {
                    if (!isDisabled) setIsOpen(true);
                }}
                onFocus={() => {
                    if (inputRef.current && !isDisabled) {
                        inputRef.current.select();
                    }
                    if (!isDisabled) setIsOpen(true);
                    if (onFocus) onFocus();
                }}
                onBlur={() => {
                    if (onBlur) onBlur();
                }}
                onKeyDown={handleKeyDown}
                placeholder={
                    loading ? '読込中...' :
                        !hasHojo ? '-' :
                            hojoList.length === 0 ? '-' :
                                '補助科目'
                }
                disabled={isDisabled}
                style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '4px 4px',
                    border: '2px inset #c0c0c0',
                    fontFamily: 'MS UI Gothic, sans-serif',
                    fontSize: '13px',
                    outline: 'none',
                    backgroundColor: isDisabled ? '#f0f0f0' : 'white',
                    color: isDisabled ? '#999' : 'black',
                }}
            />

            {/* ドロップダウン */}
            {isOpen && hojoList.length > 0 && createPortal(
                <ul
                    ref={listRef}
                    style={{
                        position: 'fixed',
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`,
                        maxHeight: '250px',
                        overflowY: 'auto',
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        zIndex: 99999,
                        boxShadow: '2px 2px 10px rgba(0,0,0,0.3)',
                    }}
                >
                    {/* 「なし」オプション */}
                    <li
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleClear();
                        }}
                        onMouseEnter={() => setHighlightedIndex(0)}
                        style={{
                            padding: '6px 10px',
                            cursor: 'pointer',
                            backgroundColor: highlightedIndex === 0 ? '#0066cc' : 'white',
                            color: highlightedIndex === 0 ? 'white' : '#666',
                            fontSize: '12px',
                            fontStyle: 'italic',
                            borderBottom: '1px solid #eee',
                        }}
                    >
                        （補助科目なし）
                    </li>
                    {filteredOptions.map((hojo, index) => (
                        <li
                            key={hojo.id}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(hojo);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index + 1)}
                            style={{
                                padding: '6px 10px',
                                cursor: 'pointer',
                                backgroundColor: (index + 1) === highlightedIndex ? '#0066cc' : 'white',
                                color: (index + 1) === highlightedIndex ? 'white' : 'black',
                                fontSize: '13px',
                                fontFamily: 'MS UI Gothic, sans-serif',
                            }}
                        >
                            <span style={{ fontWeight: 'bold', marginRight: '6px' }}>
                                {hojo.hojo_code}
                            </span>
                            {hojo.hojo_name}
                        </li>
                    ))}
                </ul>,
                document.body
            )}
        </div>
    );
};

export default HojoSelect;
